import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import { ContentUpgrades } from "@swizec/gatsby-theme-course-platform";
export const _frontmatter = {
  "title": "Go full-stack in 5min with your first cloud function",
  "description": "How *do* you build a JavaScript function that runs in the cloud serverlessly? Here's how to get started with Vercel, Netlify, or AWS Lambda",
  "published": "2021-03-18T00:00:00.000Z",
  "hero": "./img/screenshot-1616081769208.png",
  "categories": "Serverless, Technical",
  "content_upgrade": "ServerlessHandbook"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <p>{`Let's get tactical, how `}<em parentName="p">{`do`}</em>{` you build a JavaScript function that runs in the cloud serverlessly?`}</p>
    <p>{`As we said earlier, `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/if-you-can-javascript-you-can-backend"
      }}>{`the modern backend is a JavaScript function`}</a>{`. `}<em parentName="p">{`You`}</em>{` can be a full-stack engineer, no fancy sysadmin skills or kubernetes monstrosities requires ✌️`}</p>
    <p>{`There's a few ways to start:`}</p>
    <ol>
      <li parentName="ol">{`Vercel cloud functions`}</li>
      <li parentName="ol">{`Netlify cloud functions`}</li>
      <li parentName="ol">{`AWS Lambda`}</li>
    </ol>
    <p>{`Vercel and Netlify are quick and easy, AWS Lambda gives you more control as you grow. All 3 let you take a function like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`export const handler = async () => {
  return {
    statusCode: 200,
    body: "Hello 👋",
  }
}
`}</code></pre>
    <p>{`And turn it into `}<a parentName="p" {...{
        "href": "https://z7pc0lqnw9.execute-api.us-east-1.amazonaws.com/dev/hello?name=Friend"
      }}>{`a publicly accessible URL that says hi`}</a>{`.`}</p>
    <ContentUpgrades.ServerlessHandbook />
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/df1689ebd53637b848f694728e5ae438/6a068/d0YNJMgifh7a8ca.jpg",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "81.61434977578475%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'327\\'%20viewBox=\\'0%200%20400%20327\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M154%2048l-26%201c-22%200-62%203-70%204-17%205-24%2013-28%2035-2%2010-3%2069-2%2096%203%2076%203%2079%2013%2088%2011%2011%2027%2014%2072%2016a3469%203469%200%2000201-3c29-1%2044-6%2050-16l6-13c2-9%202-10%202-41a839%20839%200%2000-6-129c0-28-6-33-45-37-15-2-23-2-87-2l-80%201m34%204c-36%200-93%203-105%204-41%205-47%209-49%2034a593%20593%200%20002%20120l2%2044c4%2013%2011%2019%2032%2025%2016%204%20107%206%20162%203%20101-4%20114-6%20124-14%209-7%2013-28%2012-69a83975%2083975%200%2001-5-103c-4-36-6-38-38-43-11-1-94-2-137-1m69%2022c-2%202%201%203%207%203%2010%200%2010-2%2010%2041l1%2037h-2l-7%201c-5%200-5%200-5%202s1%203%2010%203c19%200%2024-2%2013-5l-6-1v-36c1-39%201-40-5-44-4-2-13-3-16-1m-44%202l-1%201%207%201%207%202c2%201%203%2028%202%2057v19h-6l-6%201v2c0%202%207%202%2016%202%209-1%2010-2%204-4l-4-1v-10l-1-37c-1-27-1-29-5-33-2-3-12-3-13%200M56%2077l-1%203c0%202%200%202%207%202l7%201%201%205v56l-1%2016h-5c-5%201-9%203-8%205%201%201%2027%200%2030-1h2l-2-2-7-2-4-1-1-15v-14h41l1%202v27h-6c-6%201-6%201-6%203%201%203%2028%202%2027%200l-5-3-4-1-1-6-1-51V87h7c8%200%2010-1%207-4-4-3-28-2-28%202l3%201%205%201%203%201v35h-14l-21%201-8%201v-8c-1-37-2-36%209-36%206%200%207-1%204-3s-30-3-31-1m107%2030l-4%202-2%201c-2%200-5%204-9%2010-4%209-2%2025%205%2031%205%205%2020%207%2026%203%204-2%205-3%207%200%201%201%204%202%204%200l-15-15%201%204c4%204-1%208-10%208-11%200-15-4-15-15v-5l12-1c17-1%2021-3%2021-12%200-4-4-11-8-12-3-1-9-1-13%201m6%204c-6%201-15%208-15%2012%200%202%2019%201%2022-2%208-6%203-12-7-10m145%201c-10%205-14%2015-11%2027%206%2016%2025%2018%2034%202%205-9%201-23-9-29-4-3-7-3-14%200m1%205c-11%206-13%2025-2%2030%2010%204%2019-1%2022-13%203-14-7-23-20-17m23%2058a247%20247%200%20005%2054c2%200%201-48-1-52-1-4-4-4-4-2m-57%207c-1%203%204%205%2011%205%208%200%208%200%208%2014v10l-6-1c-15-1-25%208-25%2024%200%2013%2013%2021%2025%2016l5-1%203%202c3%203%208%205%2014%204%205-1%203-3-4-4h-6v-30c0-34%200-33-7-37-4-3-16-4-18-2m-50%201l-1%203c0%201%202%202%209%202h8v5l1%2033v28l-5%201c-6%200-7%201-5%204%201%201%2021%202%2024%200%202-1%200-3-5-4l-5-1v-33c-1-33-1-35-7-38h-14m-119%2010c-7%201-7%203-2%203l4%201v16a235%20235%200%2001-4%2034c-1%200-6-9-6-12%200-4-7-16-10-16-1%200-4%207-3%208l-3%208-3%208-1%201-1%202c1%203-3%205-4%203l-2-9a639%20639%200%2000-9-38l5-1%204-1c0-4-20-3-21%200%200%202%201%202%204%202h5l1%205%203%2017c7%2031%209%2038%2013%2033%203-3%207-12%2010-22%204-9%205-9%208%202%203%209%2010%2020%2012%2018%202-3%207-37%207-55%200-2%201-2%204-2s4-1%201-3c-2-2-6-3-12-2m93%2016l-6%203c-3%203-4%203-7%200-3-2-10-3-11%200%200%202%200%202%205%202l4%201v14l1%2016c0%202%200%203-4%203-5%201-6%202-4%204s19%202%2021%200c2-1-3-4-6-4h-3l1-10c0-15%201-16%204-20%204-3%209-4%2015-4%205%201%205%200%202-3-3-2-9-3-12-2m-64%202l-3%202c-4%201-10%2010-10%2016%200%2014%208%2023%2020%2023s23-12%2021-22c-1-4-9-15-12-17-4-3-16-4-16-2m4%205c-13%204-18%2025-7%2030%208%203%2022-1%2025-6%206-12-6-28-18-24m140%200c-10%205-14%2020-8%2028%203%204%207%204%2014%201%209-3%209-2%209-17v-12l-6-1-9%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/df1689ebd53637b848f694728e5ae438/ca0a1/d0YNJMgifh7a8ca.webp 223w", "/static/df1689ebd53637b848f694728e5ae438/75680/d0YNJMgifh7a8ca.webp 445w", "/static/df1689ebd53637b848f694728e5ae438/8d1ba/d0YNJMgifh7a8ca.webp 890w", "/static/df1689ebd53637b848f694728e5ae438/e46b2/d0YNJMgifh7a8ca.webp 960w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/df1689ebd53637b848f694728e5ae438/853b2/d0YNJMgifh7a8ca.jpg 223w", "/static/df1689ebd53637b848f694728e5ae438/0a4fe/d0YNJMgifh7a8ca.jpg 445w", "/static/df1689ebd53637b848f694728e5ae438/7f80b/d0YNJMgifh7a8ca.jpg 890w", "/static/df1689ebd53637b848f694728e5ae438/6a068/d0YNJMgifh7a8ca.jpg 960w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/jpeg"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/df1689ebd53637b848f694728e5ae438/7f80b/d0YNJMgifh7a8ca.jpg",
              "alt": "d0YNJMgifh7a8ca",
              "title": "d0YNJMgifh7a8ca",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <h2 {...{
      "id": "vercel"
    }}>{`Vercel`}</h2>
    <p>{`Vercel is among the youngest serverless and JAMStack providers. One of my favs for hosting websites.`}</p>
    <p>{`Take a project built in a popular web framework like Create-react-app, Gatsby, Vue, Next, Nuxt, et al, run `}<inlineCode parentName="p">{`vercel`}</inlineCode>{` and it's live on a URL. I'll never get over how easy they've made it 😍`}</p>
    <h3 {...{
      "id": "hello"
    }}>{`Hello`}</h3>
    <p>{`To make a cloud function, you use the `}<inlineCode parentName="p">{`/api`}</inlineCode>{` directory.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`;/api/ehllo.js

module.exports = (req, res) => {
  // .status sets the status code
  // .send writes the response
  return res.status(200).send("Hello 👋")
}
`}</code></pre>
    <p>{`Vercel uses Express-like functions. Express is a popular backend framework in the JavaScript world.`}</p>
    <p><inlineCode parentName="p">{`req`}</inlineCode>{` is the request object. You'll find params in here, session info, and anything you add in your chain of functions (called middleware).`}</p>
    <p><inlineCode parentName="p">{`res`}</inlineCode>{` is the response. You use it to set status codes, write the body, add headers ...`}</p>
    <p>{`Deploy, and you get a URL.`}</p>
    <p><img parentName="p" {...{
        "src": "/493de3e2fdd0222ff6eddd320b6404c3/Vercel-deploy-processe4ac2e.gif",
        "alt": "Vercel deploy process"
      }}></img></p>
    <p>{`Append `}<inlineCode parentName="p">{`/api/<your function>`}</inlineCode>{` to the URL to call your cloud function. Wakes a server on demand, runs your tiny code, returns the result 👌`}</p>
    <h3 {...{
      "id": "use-vercel"
    }}>{`Use Vercel?`}</h3>
    <p>{`Vercel's cloud functions are fantastic for tiny features. When you need to run secure code on the server, make a calculation that would freeze the browser, or call a 3rd party API or database.`}</p>
    <p>{`Primary use-case is for Next.js server-side rendering and generation. When you call `}<inlineCode parentName="p">{`getServerSideProps`}</inlineCode>{`, it's running on one of these cloud functions for you.`}</p>
    <p>{`Access to and from the full serverless ecosystem is tricky and discouraged.`}</p>
    <h2 {...{
      "id": "netlify"
    }}>{`Netlify`}</h2>
    <p>{`Netlify invented the word JAMStack and they offer Netlify Functions as their cloud function approach.`}</p>
    <p>{`Like Vercel, they're great at hosting any project built in a popular web framework. The difference is they're UI first. Not my jam, personally. The GitHub integration works great 👌`}</p>
    <h3 {...{
      "id": "hai"
    }}>{`Hai`}</h3>
    <p>{`To make a cloud function, you add a file to to the `}<inlineCode parentName="p">{`/netlify/functions/`}</inlineCode>{` directory.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`exports.handler = async () => {
  return {
    statusCode: 200,
    body: "Hello 👋",
  }
}
`}</code></pre>
    <p>{`Netlify acts as a thin wrapper on AWS Lambda and uses the same syntax. Huzzah 🙌`}</p>
    <p>{`An optional `}<inlineCode parentName="p">{`event`}</inlineCode>{` argument gives you access to params, headers, and other info. You return the response as a JavaScript object.`}</p>
    <p>{`There are no middlewares.`}</p>
    <p>{`Deploy, and you get a URL.`}</p>
    <p><img parentName="p" {...{
        "src": "/aac690f0aad633b4d0b2b899b9facec5/Netlify-deploy-processh32ceh.gif",
        "alt": "Netlify deploy process"
      }}></img></p>
    <p>{`Append `}<inlineCode parentName="p">{`/.netlify/functions/<your function>`}</inlineCode>{` to the URL to call your cloud function. Wakes a server on demand, runs your tiny code, returns the result 👌`}</p>
    <h3 {...{
      "id": "use-netlify"
    }}>{`Use Netlify?`}</h3>
    <p>{`Like Vercel, Netlify cloud functions are fantastic for tiny features. Do something secret, hook up a 3rd party API, ...`}</p>
    <p>{`A nice benefit is that Netlify supports event-driven functions that they trigger for you, and there's periodic background functions.`}</p>
    <p>{`But that URL looks silly and access to and from the full serverless ecosystem remains hard.`}</p>
    <h2 {...{
      "id": "aws-lambda"
    }}>{`AWS Lambda`}</h2>
    <p>{`AWS Lambda is the original serverless provider. Often used as a synonym for serverless as a whole.`}</p>
    <p>{`And as you can imagine, AWS gives zero shits about making this easy for anyone. They're a Java shop targeting Java folk aiming at grizzled backend veterans.`}</p>
    <p>{`Ask anyone if they enjoy using AWS's native UI tools 😛`}</p>
    <p>{`Hint: why do you think there's so many alternatives running on AWS?`}</p>
    <p>{`I like to use the `}<a parentName="p" {...{
        "href": "https://www.serverless.com/"
      }}>{`Serverless Framework`}</a>{` to deal with AWS. Great balance between making common features easy and powerful features possible. You can drop down to AWS's native config any time ✌️`}</p>
    <p>{`Heard great things about `}<a parentName="p" {...{
        "href": "https://arc.codes/"
      }}>{`arc.codes`}</a>{`, haven't tried yet.`}</p>
    <h3 {...{
      "id": "aloha"
    }}>{`Aloha`}</h3>
    <p>{`You take the same JavaScript function from Netlify`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// handler.js
exports.hello = async () => {
  return {
    statusCode: 200,
    body: \`Hello 👋\`,
  }
}
`}</code></pre>
    <p>{`And add a configuration file:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-yaml"
      }}>{`# serverless.yml

service: hello-world
provider:
  name: aws
  runtime: nodejs12.x
  stage: dev

functions:
  hello:
    handler: ./handler.hello
    events:
      - http:
          path: hello
          method: GET
          cors: true
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`provider`}</inlineCode>{` section defines where and how your function runs. Serverless Framework supports more than AWS.`}</p>
    <p>{`You can use this area for shared config between functions.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`functions`}</inlineCode>{` section defines your AWS Lambda functions. Each comes with a name, a function to call, and the `}<inlineCode parentName="p">{`event`}</inlineCode>{` (or many) that trigger it.`}</p>
    <p>{`The config above creates an API Gateway that listens on a `}<inlineCode parentName="p">{`/dev/hello`}</inlineCode>{` path.`}</p>
    <p>{`Watch`}</p>
    <p><img parentName="p" {...{
        "src": "/c9c728a83784bdfd9d81ad3c3770c87f/Serverless-AWS-Lambda-deploy8bg956.gif",
        "alt": "Serverless AWS Lambda deploy"
      }}></img></p>
    <p>{`Take the URL, call your cloud function. Wakes a server on demand, runs your tiny code, returns the result 👌`}</p>
    <h3 {...{
      "id": "use-aws-lambda"
    }}>{`Use AWS Lambda`}</h3>
    <p>{`It's more work than Vercel or Netlify and you'll never outgrow it. Okay, you might turn into a Fortune100 company one day ...`}</p>
    <p>{`If you're engineering at a Fortune100, you don't need any of this. There's a department for that 🙃`}</p>
    <p>{`With AWS you can do `}<em parentName="p">{`everything`}</em>{`. The whole ecosystem is easy to access and configurable through `}<inlineCode parentName="p">{`serverless.yml`}</inlineCode>{`.`}</p>
    <p>{`You pick this if the backend is at all important to your project my friend.`}</p>
    <p>{`What would you pick? hit reply`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>
    <p>{`PS: don't forget, `}<a parentName="p" {...{
        "href": "https://www.youtube.com/watch?v=WC-mEf4K9as"
      }}>{`live Serverless Q&A and new book celebration Monday morning`}</a>{`; come say Hi`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      